import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * 组件用于更新SEO相关元素，包括页面标题、描述和语言设置
 */
const SeoUpdater = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();

  /**
   * 获取当前页面的规范URL（不带参数的主要URL）
   */
  const getCanonicalUrl = () => {
    const baseUrl = 'https://anonlinenotepad.com';
    const pathname = location.pathname;
    
    // 不同页面的规范URL映射
    const canonicalUrls = {
      '/': baseUrl + '/',
      '/blog': baseUrl + '/blog',
      '/terms': baseUrl + '/terms',
      '/privacy': baseUrl + '/privacy'
    };
    
    return canonicalUrls[pathname] || baseUrl + pathname;
  };

  /**
   * 检查页面是否应该强制显示英文版本
   */
  const shouldForceEnglish = () => {
    const pathname = location.pathname;
    return pathname === '/terms' || pathname === '/privacy';
  };

  /**
   * 获取hreflang URL映射
   */
  const getHreflangUrls = () => {
    const baseUrl = 'https://anonlinenotepad.com';
    const pathname = location.pathname;
    const supportedLanguages = ['en', 'zh', 'zh-TW', 'ja', 'de', 'fr', 'es', 'ko', 'hi', 'ar'];
    
    // 对于terms和privacy页面，只提供英文版本
    if (shouldForceEnglish()) {
      const hreflangUrls = {};
      supportedLanguages.forEach(lang => {
        hreflangUrls[lang] = baseUrl + pathname; // 所有语言都指向同一个英文页面
      });
      return hreflangUrls;
    }
    
    // 对于其他页面，提供多语言版本
    const hreflangUrls = {};
    supportedLanguages.forEach(lang => {
      hreflangUrls[lang] = pathname === '/' 
        ? `${baseUrl}/?lng=${lang}`
        : `${baseUrl}${pathname}?lng=${lang}`;
    });
    
    return hreflangUrls;
  };

  /**
   * 获取页面特定的SEO信息
   */
  const getPageSeoInfo = () => {
    const pathname = location.pathname;
    
    // Terms和Privacy页面强制使用英文信息
    if (shouldForceEnglish()) {
      const englishTranslation = {
        '/terms': {
          title: 'Terms of Service - Online Notepad',
          description: 'Terms of Service - No bells and whistles. Just a text editor with a beautifully crafted minimalist interface that helps you focus on what you need to write.'
        },
        '/privacy': {
          title: 'Privacy Policy - Online Notepad',
          description: 'Privacy Policy - No bells and whistles. Just a text editor with a beautifully crafted minimalist interface that helps you focus on what you need to write.'
        }
      };
      return englishTranslation[pathname] || {
        title: 'Online Notepad - Take Notes and Share Notes Online',
        description: 'Online Notepad is your everyday online notepad. Write down quick notes and print a simple text document with Online Notepad editor.'
      };
    }
    
    // 其他页面使用当前语言
    switch (pathname) {
      case '/':
        return {
          title: t('seo.title'),
          description: t('seo.description')
        };
      case '/blog':
        return {
          title: `${t('blog.title')} - ${t('app.title')}`,
          description: t('blog.subtitle')
        };
      default:
        return {
          title: t('seo.title'),
          description: t('seo.description')
        };
    }
  };

  /**
   * 添加结构化数据到页面头部
   * @param {Function|Object} translation - 翻译函数或翻译对象
   */
  const addStructuredData = (translation) => {
    // 移除已存在的结构化数据
    const existingStructuredData = document.getElementById('structured-data');
    if (existingStructuredData) {
      existingStructuredData.remove();
    }

    // 创建新的结构化数据
    const structuredDataScript = document.createElement('script');
    structuredDataScript.setAttribute('id', 'structured-data');
    structuredDataScript.setAttribute('type', 'application/ld+json');

    // 网站域名
    const websiteUrl = 'https://anonlinenotepad.com';

    // 处理翻译函数或对象
    const getText = (key) => {
      if (typeof translation === 'function') {
        return translation(key);
      } else {
        return translation[key] || key;
      }
    };

    // 构建结构化数据对象
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        // Google Knowledge Graph
        {
          "@context": "https://schema.org",
          "@type": "Corporation",
          "name": getText('app.title'),
          "alternateName": "An Online Notepad",
          "url": websiteUrl,
          "logo": "https://anonlinenotepad.com/android-chrome-512x512.png",
          "sameAs": [
            "https://twitter.com/anonlinenotepad",
            "https://www.facebook.com/anonlinenotepad",
            "https://www.linkedin.com/company/anonlinenotepad"
          ]
        },
        // 专门用于Logo识别的结构化数据
        {
          "@context": "https://schema.org",
          "@type": "Brand",
          "name": getText('app.title'),
          "logo": "https://anonlinenotepad.com/android-chrome-512x512.png",
          "image": "https://anonlinenotepad.com/android-chrome-512x512.png",
          "url": "https://anonlinenotepad.com"
        },
        // 网站信息
        {
          "@type": "WebSite",
          "@id": `${websiteUrl}/#website`,
          "url": websiteUrl,
          "name": getText('app.title'),
          "description": getText('seo.description'),
          "inLanguage": shouldForceEnglish() ? 'en' : i18n.language,
          "potentialAction": [
            {
              "@type": "SearchAction",
              "target": `${websiteUrl}/?s={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          ],
          "siteMap": "https://anonlinenotepad.com/sitemap.xml"
        },
        // 组织信息
        {
          "@type": "Organization",
          "@id": `${websiteUrl}/#organization`,
          "name": getText('app.title'),
          "url": websiteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://anonlinenotepad.com/android-chrome-512x512.png",
            "width": "512",
            "height": "512"
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://anonlinenotepad.com/android-chrome-512x512.png",
            "width": "512",
            "height": "512"
          }
        },
        // 软件应用信息
        {
          "@type": "SoftwareApplication",
          "name": getText('app.title'),
          "operatingSystem": "All",
          "applicationCategory": "UtilitiesApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "1024"
          },
          "description": getText('app.overview') || getText('seo.description'),
          "featureList": [
            getText('features.autosaveDesc'),
            getText('features.wordCountDesc'),
            getText('features.findReplaceDesc'),
            getText('features.spellcheckDesc'),
            getText('features.printDesc')
          ]
        },
        // 网页信息
        {
          "@type": "WebPage",
          "@id": `${websiteUrl}/#webpage`,
          "url": websiteUrl,
          "name": getText('seo.title') || getText('app.title'),
          "isPartOf": {
            "@id": `${websiteUrl}/#website`
          },
          "about": {
            "@id": `${websiteUrl}/#organization`
          },
          "description": getText('seo.description'),
          "inLanguage": shouldForceEnglish() ? 'en' : i18n.language,
          "mainEntity": {
            "@type": "SoftwareApplication",
            "name": getText('app.title'),
            "description": getText('app.overview') || getText('seo.description'),
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "All",
            "featureList": [
              "Feature Overview",
              "Overview",
              "Complete Feature Guide"
            ]
          }
        }
      ]
    };

    structuredDataScript.textContent = JSON.stringify(structuredData);
    document.head.appendChild(structuredDataScript);
  };

  useEffect(() => {
    const pageInfo = getPageSeoInfo();
    const canonicalUrl = getCanonicalUrl();
    const hreflangUrls = getHreflangUrls();
    
    // 对于Terms和Privacy页面，强制切换到英文
    if (shouldForceEnglish() && i18n.language !== 'en') {
      i18n.changeLanguage('en');
      return; // 等待语言切换完成后重新触发effect
    }
    
    // 更新HTML lang属性
    document.documentElement.lang = shouldForceEnglish() ? 'en' : i18n.language;

    // 更新页面标题
    const pageTitle = document.getElementById('page-title');
    if (pageTitle) {
      pageTitle.innerText = pageInfo.title;
    } else {
      document.title = pageInfo.title;
    }

    // 更新Meta描述
    const metaDescription = document.getElementById('meta-description');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageInfo.description);
    }

    // 更新canonical链接 - 始终指向不带参数的规范URL
    const canonicalElement = document.getElementById('canonical-url');
    if (canonicalElement) {
      canonicalElement.setAttribute('href', canonicalUrl);
    }

    // 更新Open Graph URL
    const ogUrlElement = document.querySelector('meta[property="og:url"]');
    if (ogUrlElement) {
      ogUrlElement.setAttribute('content', canonicalUrl);
    }

    // 更新hreflang链接
    const supportedLanguages = ['en', 'zh', 'zh-TW', 'ja', 'de', 'fr', 'es', 'ko', 'hi', 'ar'];
    supportedLanguages.forEach(lang => {
      const hreflangElement = document.getElementById(`hreflang-${lang}`);
      if (hreflangElement) {
        hreflangElement.setAttribute('href', hreflangUrls[lang]);
      }
    });

    // 更新x-default链接 - 指向当前页面的默认版本
    const xDefaultElement = document.querySelector('link[hreflang="x-default"]');
    if (xDefaultElement) {
      xDefaultElement.setAttribute('href', canonicalUrl);
    }

    // 添加结构化数据 JSON-LD
    addStructuredData(shouldForceEnglish() ? 
      // Terms和Privacy页面使用固定英文结构化数据
      {
        'app.title': 'Online Notepad',
        'app.overview': 'Online Notepad is a free browser-based text editor that allows you to create and edit multiple plain-text files in your browser. No registration or login required.',
        'seo.title': 'Online Notepad - Take Notes and Share Notes Online',
        'seo.description': 'Online Notepad is your everyday online notepad. Write down quick notes and print a simple text document with Online Notepad editor. It includes spellchecker, word counter, autosave, find and replace etc.',
        'features.autosaveDesc': 'Never lose your notes with its auto save feature. Every time you make changes, it will automatically store the data locally within your browser.',
        'features.wordCountDesc': 'Track how many words there are in your text document with real-time word counter. The number of words will update as you type and make changes.',
        'features.findReplaceDesc': 'Find and replace allows you to quickly search for a word or phrase in the whole page and substitute it with something else.',
        'features.spellcheckDesc': 'It takes advantage of your browser\'s native spellchecker to instantly find spelling mistakes. Simply right-click the red underlined word and choose the correct spelling.',
        'features.printDesc': 'Getting a hard copy of your notes has never been easier. You can prepare the document for printing or save it as a PDF file with a single click of a button.'
      } : t
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, location.pathname]);



  return null;
};

export default SeoUpdater;